// US-069: Per-template PO column configuration — shared client helper.
//
// A "template" is a named PO line-item column set (which columns are enabled +
// their order), mirroring the master `po_line_item_columns` config. The master
// config is exposed by the backend as the template with id "default".
//
// This module holds the pure, unit-testable pieces shared by the settings page,
// the PO detail view, and the export/send dialogs. It performs no I/O.

export const DEFAULT_TEMPLATE_ID = 'default';

export interface ColumnConfig {
  enabled: boolean;
  order: number;
}

export interface MetafieldColumnConfig {
  enabled: boolean;
  order: number;
  label: string;
  namespace: string;
  name: string;
  ownerResource?: string;
}

export interface ColumnTemplate {
  id: string;
  name: string;
  isDefault: boolean;
  columns: { [columnId: string]: ColumnConfig };
  metafieldColumns: { [key: string]: MetafieldColumnConfig };
}

/**
 * Normalize a `GET /purchase_orders/config/column-templates` response (or a raw
 * array) into a well-formed ColumnTemplate[]. Missing maps default to {}, and a
 * template whose id is "default" is always flagged isDefault.
 */
export function shapeTemplateList(raw: any): ColumnTemplate[] {
  const list = Array.isArray(raw?.templates)
    ? raw.templates
    : (Array.isArray(raw) ? raw : []);

  return list
    .filter((t: any) => t && typeof t.id === 'string')
    .map((t: any) => ({
      id: t.id,
      name: typeof t.name === 'string' ? t.name : '',
      isDefault: t.isDefault === true || t.id === DEFAULT_TEMPLATE_ID,
      columns: t.columns && typeof t.columns === 'object' && !Array.isArray(t.columns) ? t.columns : {},
      metafieldColumns: t.metafieldColumns && typeof t.metafieldColumns === 'object' && !Array.isArray(t.metafieldColumns) ? t.metafieldColumns : {},
    }));
}

/**
 * Resolve a template by id, mirroring the backend fallback rule:
 *   - missing / "default" id  -> the default (master) template
 *   - unknown id              -> the default (master) template
 * Returns null only when the list has no default and no match (e.g. empty list).
 */
export function resolveTemplate(
  templates: ColumnTemplate[],
  id: string | null | undefined,
): ColumnTemplate | null {
  if (!Array.isArray(templates) || templates.length === 0) return null;

  const wantedId = !id ? DEFAULT_TEMPLATE_ID : id;
  const exact = templates.find(t => t.id === wantedId);
  if (exact) return exact;

  // Unknown/stale id -> fall back to the default/master template.
  return templates.find(t => t.id === DEFAULT_TEMPLATE_ID)
    ?? templates.find(t => t.isDefault)
    ?? null;
}

/**
 * The list of enabled MasterData/PO column ids for a template, sorted by order.
 * Used to drive the export `optionalColumns` param and the detail-view fetch.
 */
export function enabledColumnIds(template: ColumnTemplate | null): string[] {
  if (!template || !template.columns) return [];
  return Object.entries(template.columns)
    .filter(([, config]) => config && config.enabled)
    .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
    .map(([columnId]) => columnId);
}
